import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Modal, { ModalHeader, ModalBody, ModalFooter } from 'core-system/Modal'
import { Card, Payment } from 'redux/employeePlatform/employeePlatformTypes'
import FleetCard from './FleetCard'
import Divider from 'core-system/Divider'
import palette from 'core-system/Themes/palette'
import Text from 'core-system/Text'
import LockIcon from 'core-system/Icons/Misc/Lock'
import UnlockIcon from 'core-system/Icons/Misc/Unlock'
import AddIcon from 'core-system/Icons/Actions/Add'
import { useDispatch } from 'react-redux'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import pxToRem from 'core-system/utils/pxToRem'
import { NumberFormatter, NumberInput } from 'core-system/Input/NumberInput'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import Radio from 'core-system/Radio'
import FormattingUtils from 'shared/FormattingUtils'
import { convertToTitleCase } from 'employee-platform/shared/utils'
import { ApiServerUrl } from 'redux/config/services/Constants'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { useGateValue } from '@statsig/react-bindings'

const Container = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: ${(props) => (props.isMobile ? 0 : '0.5rem')};
`

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const TopUpAmountContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const PaymentMethodContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`

const TopUpBreakdownItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const ModalBodyContainer = styled(ModalBody)`
  padding: 1rem 2rem;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`

const isBankAccountPaymentMethod = (type: string) => type !== 'card'

const renderPaymentMethods = (
  paymentMethods: Payment[],
  selectedPaymentMethod: { internalId: string; type: string } | null,
  setSelectedPaymentMethod: (
    paymentMethod: { internalId: string; type: string } | null
  ) => void,
  hideAch: boolean
) => {
  return paymentMethods.map((paymentMethod) => {
    const isInactive =
      hideAch && isBankAccountPaymentMethod(paymentMethod.type)

    return (
      <PaymentMethodContainer
        key={paymentMethod.internalId}
        disabled={isInactive}
      >
        <Radio
          active={
            paymentMethod.internalId === selectedPaymentMethod?.internalId
          }
          disabled={isInactive}
          onClick={() => {
            if (isInactive) {
              return
            }
            setSelectedPaymentMethod({
              internalId: paymentMethod.internalId,
              type: paymentMethod.type,
            })
          }}
        />
        <Text
          variant='body1'
          textColor={isInactive ? palette.text.disabled : palette.text.primary}
        >
          {`${
            paymentMethod.type === 'card'
              ? convertToTitleCase(paymentMethod.brand)
              : convertToTitleCase(paymentMethod.bankName)
          } •••• ${paymentMethod.last4}`}
        </Text>
      </PaymentMethodContainer>
    )
  })
}

const CardDetailsIframe = ({ cardId }) => {
  if (!cardId) {
    return null
  }
  const src = `${ApiServerUrl}/payments/card-details-iframe/${cardId}`

  return (
    <iframe
      src={src}
      title='Card Details'
      className='w-full h-full'
      style={{
        border: 'none',
        overflow: 'hidden',
        height: '15rem',
        width: '100%',
      }}
      scrolling='no'
    />
  )
}

interface FleetCardDetailsModalProps {
  open: boolean
  closeModal: () => void
  card: Card
  isMobile: boolean
}

const FleetCardDetailsModal = React.memo(
  (props: FleetCardDetailsModalProps) => {
    const { open, closeModal, card, isMobile } = props

    const { paymentMethods } = useSelector(
      (state: AppState) => state.employeePlatform.wallet
    )

    const dispatch = useDispatch()
    const hideAch = useGateValue('hide_ach')

    const [showFullDetails, setShowFullDetails] = useState(false)
    const [currentView, setCurrentView] = useState<
      'details' | 'lock' | 'addfunds'
    >('details')
    const [topUpAmount, setTopUpAmount] = useState(0)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{
      internalId: string
      type: string
    } | null>(null)

    const getTitle = () => {
      if (currentView === 'details') {
        return 'Fleet Card Details'
      } else if (currentView === 'lock') {
        return `${card?.status !== 'active' ? 'Unlock' : 'Lock'} Fleet Card`
      } else {
        return 'Add Funds'
      }
    }

    const getConfirmButtonText = () => {
      if (currentView === 'lock') {
        return card?.status !== 'active' ? 'Unlock' : 'Lock'
      } else {
        return 'Add Funds'
      }
    }

    const handleTopUpAmountChange = (amount: number) => {
      setTopUpAmount(amount * 100)
    }

    const handleClose = () => {
      closeModal()
      setShowFullDetails(false)
      setCurrentView('details')
      setTopUpAmount(0)
      setSelectedPaymentMethod(null)
    }

    const handleConfirm = () => {
      if (currentView === 'lock') {
        if (card?.status !== 'active') {
          dispatch(employeePlatformActions.unlockCard(card?.internalId))
        } else {
          dispatch(employeePlatformActions.lockCard(card?.internalId))
        }
      } else {
        dispatch(
          employeePlatformActions.topUpCard({
            paymentMethodId: selectedPaymentMethod?.internalId,
            topUpAmount: topUpAmount,
          })
        )
      }
      handleClose()
    }

    const renderShowCardDetailsButton = () => {
      if (showFullDetails) {
        return (
          <SecondaryButton onClick={() => setShowFullDetails(false)}>
            Hide Card Details
          </SecondaryButton>
        )
      }
      return (
        <SecondaryButton onClick={() => setShowFullDetails(true)}>
          Show Card Details
        </SecondaryButton>
      )
    }

    return (
      <Modal open={open} onClose={() => handleClose()} width={pxToRem(450)}>
        <ModalHeader title={getTitle()} />
        <ModalBodyContainer>
          {currentView === 'details' ? (
            <Container isMobile={isMobile}>
              {showFullDetails ? (
                <CardDetailsIframe cardId={card?.id} />
              ) : (
                <>
                  <CardContainer>
                    <FleetCard card={card} size='xlarge' />
                  </CardContainer>
                </>
              )}
              <DetailsSection>
                {card?.type === 'virtual' ? (
                  renderShowCardDetailsButton()
                ) : (
                  <Text variant='body2'>
                    Physical card details are not available on the web. Please
                    refer to your physical card for details.
                  </Text>
                )}
              </DetailsSection>
              <ButtonsContainer>
                <PrimaryButton
                  startIcon={<AddIcon />}
                  onClick={() => setCurrentView('addfunds')}
                  fullWidth
                >
                  Add Funds
                </PrimaryButton>
                <SecondaryButton
                  startIcon={
                    card?.status !== 'active' ? <UnlockIcon /> : <LockIcon />
                  }
                  onClick={() => setCurrentView('lock')}
                  fullWidth
                >
                  {card?.status !== 'active' ? 'Unlock' : 'Lock'}
                </SecondaryButton>
              </ButtonsContainer>
            </Container>
          ) : null}
          {currentView === 'lock' ? (
            <Container isMobile={isMobile}>
              <Text variant='h4'>
                {`Are you sure you want to ${
                  card?.status !== 'active' ? 'unlock' : 'lock'
                } your card?`}
              </Text>
              <Text variant='body1' textColor={palette.text.secondary}>
                {card?.status !== 'active'
                  ? 'Unlocking your card will enable your card and allow you to make transactions with it'
                  : 'Locking your card will be able disable your card and all future purchases using this card will be declined'}
              </Text>
            </Container>
          ) : null}
          {currentView === 'addfunds' ? (
            <Container isMobile={isMobile}>
              <TopUpAmountContainer>
                <Text variant='body1'>Amount</Text>
                <NumberInput
                  value={topUpAmount / 100}
                  onValueChange={handleTopUpAmountChange}
                  placeholder='0.00'
                  prefix='$'
                  incrementVal={5}
                >
                  <NumberFormatter decimalScale={0} fontVariant='h3' />
                </NumberInput>
                <Text variant='body2' textColor={palette.text.secondary}>
                  Minimum $5
                </Text>
              </TopUpAmountContainer>
              <Divider marginY='0.5rem' />
              <Text variant='action3'>Payment Method</Text>
              {renderPaymentMethods(
                paymentMethods,
                selectedPaymentMethod,
                setSelectedPaymentMethod,
                hideAch
              )}
              {hideAch &&
              paymentMethods.some((paymentMethod) =>
                isBankAccountPaymentMethod(paymentMethod.type)
              ) ? (
                <Text variant='body2' textColor={palette.text.secondary}>
                  Payments via a bank account are temporarily disabled.
                </Text>
              ) : null}
              <Divider marginY='0.5rem' />
              <Text variant='action3'>Breakdown</Text>
              <TopUpBreakdownItem>
                <Text variant='body1'>Top Up Amount</Text>
                <Text variant='body1'>
                  {FormattingUtils.formatDollar(topUpAmount)}
                </Text>
              </TopUpBreakdownItem>
              {selectedPaymentMethod?.type === 'card' ? (
                <TopUpBreakdownItem>
                  <Text variant='body1'>{'Card Processing Fees (3%)'}</Text>
                  <Text variant='body1'>
                    {FormattingUtils.formatDollar(topUpAmount * 0.03)}
                  </Text>
                </TopUpBreakdownItem>
              ) : null}
              <Divider />
              <TopUpBreakdownItem>
                <Text variant='action4'>Total</Text>
                <Text variant='action4'>
                  {FormattingUtils.formatDollar(
                    selectedPaymentMethod?.type === 'card'
                      ? topUpAmount * 1.03
                      : topUpAmount
                  )}
                </Text>
              </TopUpBreakdownItem>
            </Container>
          ) : null}
        </ModalBodyContainer>
        {currentView !== 'details' ? (
          <ModalFooter>
            <SecondaryButton onClick={() => handleClose()}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => handleConfirm()}
              disabled={
                currentView === 'addfunds' &&
                (!selectedPaymentMethod || topUpAmount < 500)
              }
              style={{ marginLeft: '0.5rem' }}
            >
              {getConfirmButtonText()}
            </PrimaryButton>
          </ModalFooter>
        ) : null}
      </Modal>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  FleetCardDetailsModal.displayName = 'FleetCardDetailsModal'
}

export default FleetCardDetailsModal
